import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap rounded-full text-sm font-medium transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-dark dark:focus-visible:ring-dark-text focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default:
          'bg-brand-dark text-white hover:bg-[#404040] dark:bg-dark-text dark:text-dark-bg dark:hover:bg-gray-300 active:scale-[0.98]',
        secondary:
          'bg-brand-light text-brand-dark hover:bg-gray-200 dark:bg-dark-surface dark:text-dark-text dark:hover:bg-dark-bg active:scale-[0.98]',
        outline:
          'border-2 border-brand-dark text-brand-dark hover:bg-brand-dark hover:text-white dark:border-dark-border dark:text-dark-text dark:hover:bg-dark-surface dark:hover:text-dark-text active:scale-[0.98]',
        ghost:
          'text-brand-dark hover:bg-brand-light dark:text-dark-text dark:hover:bg-dark-surface active:scale-[0.98]',
        link:
          'text-brand-dark underline-offset-4 hover:underline dark:text-dark-text',
        destructive:
          'bg-error-text text-white hover:bg-red-700 dark:bg-red-600 dark:hover:bg-red-700 active:scale-[0.98]',
        // Landing page variants
        hero:
          'bg-accent text-accent-foreground hover:opacity-90 active:scale-[0.98] font-ui font-semibold shadow-lg hover:shadow-xl',
        'hero-outline':
          'border-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground active:scale-[0.98] font-ui font-semibold',
      },
      size: {
        default: 'h-11 px-6 py-2',
        sm: 'h-9 px-4 text-xs',
        lg: 'h-14 px-8 text-base',
        xl: 'h-16 px-10 text-lg',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
